import { FileViewerButton, createStyles, withStyles, Dialog, FilePreview, IconButton } from '@paintscout/ui'
import type { MuiTheme as Theme, WithStyles, WithTheme, DialogProps } from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import type { QuoteFile } from 'paintscout'
import React from 'react'
import SwipeableViews from 'react-swipeable-views'
import ItemFilesStepper from '../EditItemFilesDialog/ItemFilesStepper'

const styles = (theme: Theme) =>
  createStyles({
    paper: {
      margin: 'auto'
    },
    scrollPaper: {
      overflow: 'hidden',
      maxHeight: 'none',
      // dvh tracks the *visible* viewport; `vh` resolves against the larger
      // frame (toolbars hidden) on iOS/iPadOS Safari, so a top-anchored 95vh
      // dialog extended below the visible fold and pushed the stepper/buttons
      // off-screen in iPad landscape (#21015). `vh` stays as the fallback for
      // browsers without dvh support (Safari <15.4 etc.) so the dialog never
      // collapses; @supports upgrades to dvh where available. Same precedent as
      // FileViewerDialog / PreviewPopper.
      height: '95vh',
      '@supports (height: 95dvh)': {
        height: '95dvh'
      },
      '&$paperFullScreen': {
        height: '100vh',
        '@supports (height: 100dvh)': {
          height: '100dvh'
        }
      }
    },
    paperFullScreen: {},
    content: {
      position: 'relative',
      height: '100%',
      display: 'flex',
      maxWidth: '100%',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    },
    swipeContainer: {
      flexGrow: 1,
      // Without minHeight: 0 a flex item can't shrink below its content's
      // intrinsic height. In landscape (short viewport) the image area would
      // then overflow the 95vh content box and, since it's centered with
      // overflow: hidden, clip the stepper/buttons off the bottom (#21015).
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      '& .react-swipeable-view-container': {
        flexGrow: 1
      }
    },
    hiddenFilePreview: {
      display: 'none'
    },
    currentFilePreview: {
      height: '100%',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      padding: '80px',
      [theme.breakpoints.down('sm')]: {
        padding: '10px'
      },
      // iPad landscape is wide enough to miss the `sm` width breakpoint, so key
      // off viewport height to give the image room without cramping the
      // stepper (#21015).
      '@media (max-height: 700px)': {
        padding: '16px'
      }
    },
    image: {
      objectFit: 'contain'
    },

    closeDialogButton: {
      position: 'absolute',
      top: 10,
      right: 10,
      zIndex: 1400
    },
    description: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1.5, 2),
      width: '100%',
      flexShrink: 0,
      background: theme.palette.common.white
    },
    stepper: {
      // Always reserve the stepper/buttons row; never let it be compressed or
      // pushed out of the (overflow: hidden) content box (#21015).
      flexShrink: 0
    },
    descriptionTitle: {
      ...theme.typography.subtitle1,
      fontWeight: theme.typography.fontWeightMedium,
      color: theme.paintscout.palette.brand.Neutral.P30
    },
    descriptionCaption: {
      ...theme.typography.body2,
      color: theme.paintscout.palette.brand.Neutral.P50
    }
  })

export interface FilesLightboxDialogProps extends DialogProps {
  files: QuoteFile[]
  index?: number
}

export type FilesLightboxDialogPropsWithInjections = FilesLightboxDialogProps & WithStyles<typeof styles> & WithTheme

export interface FilesLightboxDialogState {
  index: number
}

function FilesLightboxDialog(props: FilesLightboxDialogPropsWithInjections) {
  const { classes, theme: _theme, files, onClose, ...dialogProps } = props
  const [index, setIndex] = React.useState(props.index || 0)

  const currentFile = files[index]

  const nextButton = (
    <FileViewerButton
      tone="neutral"
      onClick={() => {
        setIndex(index + 1)
      }}
      disabled={index === files.length - 1}
    >
      Next
      <KeyboardArrowRightIcon />
    </FileViewerButton>
  )

  const backButton = (
    <FileViewerButton
      tone="neutral"
      onClick={() => {
        setIndex(index - 1)
      }}
      disabled={index === 0}
    >
      <KeyboardArrowLeftIcon />
      Back
    </FileViewerButton>
  )

  return (
    <Dialog
      classes={{
        paper: classes.paper,
        paperScrollPaper: classes.scrollPaper,
        paperFullScreen: classes.paperFullScreen
      }}
      maxWidth="lg"
      fullWidth={true}
      {...dialogProps}
    >
      <div className={classes.content}>
        <SwipeableViews
          className={classes.swipeContainer}
          axis={'x'}
          index={index}
          onChangeIndex={(newIndex) => setIndex(newIndex)}
          enableMouseEvents={true}
        >
          {files.map((file, fileIndex) => (
            <FilePreview
              key={file.key}
              file={file}
              showOverlay={false}
              classes={{
                root: index === fileIndex ? classes.currentFilePreview : classes.hiddenFilePreview,
                image: classes.image
              }}
            />
          ))}
        </SwipeableViews>
        {(currentFile?.title || currentFile?.caption) && (
          <div className={classes.description}>
            {currentFile.title && <div className={classes.descriptionTitle}>{currentFile.title}</div>}
            {currentFile.caption && <div className={classes.descriptionCaption}>{currentFile.caption}</div>}
          </div>
        )}
        <ItemFilesStepper
          className={classes.stepper}
          files={files}
          activeStep={index}
          position="static"
          nextButton={nextButton}
          backButton={backButton}
        />
        <IconButton className={classes.closeDialogButton} color="inherit" onClick={() => onClose({}, null)}>
          <CloseIcon />
        </IconButton>
      </div>
    </Dialog>
  )
}

export default withStyles(styles, { withTheme: true })(FilesLightboxDialog)
